import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  IFButton,
  IFTable,
  IFText,
  IFSkeleton,
  IFGroupingComponent,
} from 'Components'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import ReportIcon from '@mui/icons-material/Report'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'

import OperatorAlertsActions from 'Stores/Alert/Actions'
import OperatorAlertsSelectors from 'Stores/Alert/Selectors'
import { OperatorAlertModal } from 'Containers'
import Styles from './OperatorAlertList.module.css'

const OperatorAlertsList = ({
  tablesHeight,
  setClickedOperatorAlert,
  fetchOperatorsAlerts,
  operatorsAlerts,
  operatorAlertFilter,
  fetchOperatorsAlertsRequestState,
  operatorsAlertsPaginationOffset,
  deleteAlertRequestState,
  createAlertRequestState,
  resetEditingStates,
}) => {
  const { t } = useTranslation()
  const operatorAlertRef = useRef()

  const loadMoreData = () => {
    if (fetchOperatorsAlertsRequestState === RequestState.LOADING_MORE) return
    fetchOperatorsAlerts(operatorAlertFilter, operatorsAlertsPaginationOffset)
  }

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchOperatorsAlertsRequestState === RequestState.ERROR_0_NETWORK ||
      fetchOperatorsAlertsRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchOperatorsAlertsRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchOperatorsAlertsRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchOperatorsAlertsRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchOperatorsAlertsRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchOperatorsAlertsRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchOperatorsAlertsRequestState])
  const handleRetry = () => {
    fetchOperatorsAlerts(operatorAlertFilter, operatorsAlertsPaginationOffset)
  }

  const operatorAlertTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 160,
        header:
          fetchOperatorsAlertsRequestState === RequestState.LOADING ||
          operatorsAlertsPaginationOffset === 0 ? (
            <div>
              <IFSkeleton
                variant="text"
                width="110px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>{t('OperatorAlertList.Name')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < operatorsAlerts.length ? (
            <div className={Styles.ParentFlexContainer}>
              <IFText className={Styles.TableText}>{row.original.name}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'operatorUsers',
        size: 200,
        enableEditing: false,
        header:
          fetchOperatorsAlertsRequestState === RequestState.LOADING ||
          operatorsAlertsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorAlertList.OperatorUsers')}</IFText>
          ),
        Cell: ({ row }) => {
          return row.index < operatorsAlerts.length ? (
            <div>
              {row.original.emails.length === 0 ? (
                <IFText>{t('—')}</IFText>
              ) : (
                <IFGroupingComponent componentText={row.original.emails} />
              )}
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'chargePointsStatus',
        size: 200,
        header:
          fetchOperatorsAlertsRequestState === RequestState.LOADING ||
          operatorsAlertsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorAlertList.ChargePointsStatus')}</IFText>
          ),

        Cell: ({ row }) => {
          return row.index < operatorsAlerts.length ? (
            <div className={Styles.ParentFlexContainer}>
              <IFText className={Styles.TableText}>
                {Array.isArray(row.original.trackedStatus)
                  ? row.original.trackedStatus.join(',')
                  : row.original.trackedStatus}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'type',
        size: 120,
        header:
          fetchOperatorsAlertsRequestState === RequestState.LOADING ||
          operatorsAlertsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorAlertList.Type')}</IFText>
          ),

        Cell: ({ row }) => {
          return row.index < operatorsAlerts.length ? (
            <div>
              <IFText>{row.original.alertType}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'global',
        size: 120,
        header:
          fetchOperatorsAlertsRequestState === RequestState.LOADING ||
          operatorsAlertsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorAlertList.Global')}</IFText>
          ),

        Cell: ({ row }) => {
          return row.index < operatorsAlerts.length ? (
            <div>
              {row.original.isGlobal
                ? t('OperatorAlertList.Yes')
                : t('OperatorAlertList.No')}
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },

      {
        accessorKey: 'operatorAlertsStatus',
        size: 120,
        header:
          fetchOperatorsAlertsRequestState === RequestState.LOADING ||
          operatorsAlertsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorAlertList.OperatorAlertsStatus')}</IFText>
          ),

        Cell: ({ row }) => {
          return row.index < operatorsAlerts.length ? (
            <div>
              {row.original.isActive
                ? t('OperatorAlertList.Active')
                : t('OperatorAlertList.Inactive')}
            </div>
          ) : failedRequest ? (
            <IFButton
              size="sm"
              color={Colors.red}
              text={t('IFTable.TryAgain')}
              onClick={handleRetry}
            />
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [
      operatorsAlerts,
      fetchOperatorsAlerts,
      fetchOperatorsAlertsRequestState,
      failedRequest,
    ],
  )
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const handleRowClick = (rowData) => {
    operatorAlertRef.current.show(rowData)
  }
  useEffect(() => {
    if (
      deleteAlertRequestState === RequestState.SUCCEEDED ||
      createAlertRequestState === RequestState.SUCCEEDED
    ) {
      operatorAlertRef.current.dismiss()
    }
  }, [deleteAlertRequestState, createAlertRequestState])

  return (
    <div>
      {((fetchOperatorsAlertsRequestState !== RequestState.SUCCEEDED &&
        !operatorsAlertsPaginationOffset) ||
        operatorsAlerts) && (
        <>
          <IFTable
            columns={operatorAlertTableColumns}
            data={operatorsAlerts}
            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
            paginationOffset={operatorsAlertsPaginationOffset}
            filters={operatorAlertFilter}
            showSkeleton={
              fetchOperatorsAlertsRequestState === RequestState.LOADING ||
              (fetchOperatorsAlertsRequestState !== RequestState.SUCCEEDED &&
                operatorsAlertsPaginationOffset === 0)
            }
            tableMaxHeight={tablesHeight}
            tableSetHeight={'100%'}
            tableRequestState={fetchOperatorsAlertsRequestState}
            onRowClick={handleRowClick}
          />
        </>
      )}
      <OperatorAlertModal
        ref={operatorAlertRef}
        setClickedOperatorAlert={setClickedOperatorAlert}
        onDismiss={() => resetEditingStates()}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    setClickedOperatorAlert: (rowData) =>
      dispatch(OperatorAlertsActions.setClickedOperatorAlert(rowData)),

    fetchOperatorsAlerts: (filter, offset) =>
      dispatch(OperatorAlertsActions.fetchOperatorsAlerts(filter, offset)),
    resetEditingStates: () =>
      dispatch(OperatorAlertsActions.resetEditingStates()),
  }
}
const mapStateToProps = (state) => ({
  operatorsAlerts: OperatorAlertsSelectors.getOperatorsAlerts(state),
  fetchOperatorsAlertsRequestState:
    OperatorAlertsSelectors.getFetchOperatorsAlertsRequestState(state),
  operatorsAlertsPaginationOffset:
    OperatorAlertsSelectors.getOperatorsAlertsPaginationOffset(state),
  operatorAlertFilter: OperatorAlertsSelectors.getOperatorAlertFilter(state),
  deleteAlertRequestState:
    OperatorAlertsSelectors.getDeleteAlertRequestState(state),
  createAlertRequestState:
    OperatorAlertsSelectors.getCreateAlertRequestState(state),
})
OperatorAlertsList.propTypes = {
  tablesHeight: PropTypes.string.isRequired,
  setClickedOperatorAlert: PropTypes.func.isRequired,
  fetchOperatorsAlerts: PropTypes.func.isRequired,
  operatorsAlerts: PropTypes.array.isRequired,
  operatorAlertFilter: PropTypes.object.isRequired,
  fetchOperatorsAlertsRequestState: PropTypes.string.isRequired,
  operatorsAlertsPaginationOffset: PropTypes.number.isRequired,
}
export default connect(mapStateToProps, mapDispatchToProps)(OperatorAlertsList)
